import React, { useState } from 'react';
import { exportPDFReport } from '../services/api';
import './PerformanceMetrics.css';

const PerformanceMetrics = ({ results, riskMetrics, onRunBacktest }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);

  const handleExportReport = async () => {
    setIsExporting(true);
    setExportMessage(null);

    try {
      const exportData = {
        weights: results.weights,
        expected_return: results.expected_return,
        volatility: results.volatility,
        sharpe_ratio: results.sharpe_ratio,
        max_drawdown: results.max_drawdown,
        sortino_ratio: results.sortino_ratio,
        risk_metrics: results.risk_metrics,
        data_info: results.data_info,
        optimization_params: results.optimization_params
      };

      const { filename, clientId } = await exportPDFReport(exportData);
      setExportMessage({
        type: 'success',
        text: `Report saved! Client ID: ${clientId}`
      });

      // Clear success message after 5 seconds
      setTimeout(() => setExportMessage(null), 5000);
    } catch (error) {
      setExportMessage({
        type: 'error',
        text: 'Failed to generate report. Please try again.'
      });
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };
  const formatPercentage = (value) => {
    if (typeof value === 'object') return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'object') return 'N/A';
    return value.toFixed(4);
  };

  return (
    <div className="performance-metrics">
      <div className="metrics-grid">
        {/* Return Metrics */}
        <div className="metric-card return-metrics">
          <h3>📈 Return Metrics</h3>
          <div className="metric-items">
            <div className="metric-item">
              <span className="label">Expected Annual Return</span>
              <span className="value positive">{formatPercentage(results.expected_return)}</span>
            </div>
            <div className="metric-item">
              <span className="label">Sharpe Ratio</span>
              <span className="value">{formatNumber(results.sharpe_ratio)}</span>
              <span className="description">Risk-adjusted return measure</span>
            </div>
            {results.sortino_ratio && (
              <div className="metric-item">
                <span className="label">Sortino Ratio</span>
                <span className="value">{formatNumber(results.sortino_ratio)}</span>
                <span className="description">Downside risk-adjusted return</span>
              </div>
            )}
          </div>
        </div>

        {/* Risk Metrics */}
        <div className="metric-card risk-metrics">
          <h3>⚠️ Risk Metrics</h3>
          <div className="metric-items">
            <div className="metric-item">
              <span className="label">Annual Volatility</span>
              <span className="value negative">{formatPercentage(results.volatility)}</span>
              <span className="description">Standard deviation of returns</span>
            </div>
            {results.max_drawdown && (
              <div className="metric-item">
                <span className="label">Maximum Drawdown</span>
                <span className="value negative">
                  {formatPercentage(results.max_drawdown)}
                </span>
                <span className="description">Largest peak-to-trough decline</span>
              </div>
            )}
          </div>
        </div>

        {/* Advanced Risk Metrics */}
        {riskMetrics && (
          <div className="metric-card advanced-metrics">
            <h3>📊 Advanced Risk Analysis</h3>
            <div className="metric-items">
              {riskMetrics.var_95 && (
                <div className="metric-item">
                  <span className="label">Value at Risk (95%)</span>
                  <span className="value negative">
                    {Object.values(riskMetrics.var_95)[0] ? 
                      formatPercentage(Object.values(riskMetrics.var_95)[0]) : 'N/A'}
                  </span>
                  <span className="description">95% confidence worst daily loss</span>
                </div>
              )}
              {riskMetrics.downside_deviation && (
                <div className="metric-item">
                  <span className="label">Downside Deviation</span>
                  <span className="value negative">
                    {Object.values(riskMetrics.downside_deviation)[0] ? 
                      formatPercentage(Object.values(riskMetrics.downside_deviation)[0]) : 'N/A'}
                  </span>
                  <span className="description">Volatility of negative returns</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Portfolio Quality */}
        <div className="metric-card quality-metrics">
          <h3>✨ Portfolio Quality</h3>
          <div className="metric-items">
            <div className="metric-item">
              <span className="label">Diversification</span>
              <span className="value">{results.data_info.num_funds} funds</span>
            </div>
            <div className="metric-item">
              <span className="label">Data Quality</span>
              <span className="value">{results.data_info.num_days} trading days</span>
              <span className="description">Historical data points used</span>
            </div>
            <div className="metric-item">
              <span className="label">Optimization Method</span>
              <span className="value">{results.optimization_params.objective}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Interpretation */}
      <div className="performance-interpretation">
        <h4>📋 Performance Interpretation</h4>
        <ul>
          {results.sharpe_ratio > 1.5 && (
            <li className="positive">✅ Excellent Sharpe ratio indicates strong risk-adjusted returns</li>
          )}
          {results.sharpe_ratio > 1 && results.sharpe_ratio <= 1.5 && (
            <li className="positive">✅ Good Sharpe ratio shows favorable risk-return balance</li>
          )}
          {results.sharpe_ratio <= 1 && (
            <li className="neutral">⚠️ Moderate Sharpe ratio - consider reviewing risk tolerance</li>
          )}
          {results.volatility < 0.15 && (
            <li className="positive">✅ Low volatility portfolio suitable for conservative investors</li>
          )}
          {results.volatility >= 0.15 && results.volatility < 0.25 && (
            <li className="neutral">⚠️ Moderate volatility - suitable for balanced investors</li>
          )}
          {results.volatility >= 0.25 && (
            <li className="negative">❌ High volatility - suitable only for aggressive investors</li>
          )}
        </ul>
      </div>

      {/* Action Items */}
      <div className="action-items">
        <h4>Recommended Actions</h4>
        {exportMessage && (
          <div className={`export-message ${exportMessage.type}`}>
            {exportMessage.type === 'success' ? '✓' : '✗'} {exportMessage.text}
          </div>
        )}
        <div className="actions">
          <button className="action-button btn-aqua">
            Save Portfolio
          </button>
          <button className="action-button btn-teal" onClick={onRunBacktest}>
            Run Backtest
          </button>
          <button
            className={`action-button btn-violet ${isExporting ? 'loading' : ''}`}
            onClick={handleExportReport}
            disabled={isExporting}
          >
            {isExporting ? 'Generating...' : 'Export Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;